import React, {Component, ReactNode} from 'react'
import ResidentGeneralMeetingRepo, {CheckUnitOwnerConfirmationKeyResponse} from './ResidentGeneralMeetingRepo'
import {currentMedia, HideInPC, HideInSP} from '../../styledComponents/ResponsiveConvenience'
import PPStepper from '../shared/PPStepper'
import styled from 'styled-components'
import {StyledPageTitle} from '../reservations/components/CommonStyle'
import {PPTextFieldForResident} from '../shared/forms/resident/PPTextFieldForResident'
import NatResidentApplicationDetailFormItem
    from '../../__NoAutomatedTests/components/applications/common/resident/NatResidentApplicationDetailFormItem'
import {
    WideWhiteArea
} from '../../__NoAutomatedTests/components/applications/common/resident/NatResidentApplicationsCommon'
import {PrimaryStyledButton, StyledSecondaryButton} from '../../styledComponents/Button'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet
} from '../shared/hoc/createResidentWithErrorHandlingPage'
import {Nullable} from '../../app/TypeHelpers'
import {Colors, FontSize} from '../../styledComponents/Constants'
import {
    unitOwnerConfirmationStepsForPc,
    unitOwnerConfirmationStepsForSp
} from './ResidentUnitOwnerConfirmationVerificationMethodSelectionPage'
import {DisplayItemDividerForPageTop} from '../../styledComponents/DisplayItem'
import {PlainBackLink} from '../../styledComponents/Links'
import {Location} from 'react-router-dom'
import {Box} from '@mui/material'

interface ResidentUnitOwnerConfirmationKeyInputLocationState {
    rootPageUrl: string
}

interface Props {
    location: Location<ResidentUnitOwnerConfirmationKeyInputLocationState>
    residentGeneralMeetingRepo: ResidentGeneralMeetingRepo
}

interface State {
    unitOwnerConfirmationKey: string
}

class ResidentUnitOwnerConfirmationKeyInputPage
    extends Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {
    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)
        this.state = {
            unitOwnerConfirmationKey: '',
        }
        this.handleUnitOwnerConfirmationKeyInputChange = this.handleUnitOwnerConfirmationKeyInputChange.bind(this)
        this.handleConfirmButtonClick = this.handleConfirmButtonClick.bind(this)
        this.handleBackToVerificationMethodSelectionButtonClick
            = this.handleBackToVerificationMethodSelectionButtonClick.bind(this)
    }

    handleUnitOwnerConfirmationKeyInputChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        this.setState({
            unitOwnerConfirmationKey: event.target.value,
        })
    }

    handleConfirmButtonClick = async () => {
        await this.props.eventEmitter.showLoading()
        try {
            const response: Nullable<CheckUnitOwnerConfirmationKeyResponse> = await this.props.asyncCallFunction(
                this.props.residentGeneralMeetingRepo.checkUnitOwnerConfirmationKey.bind(
                    this.props.residentGeneralMeetingRepo,
                    this.state.unitOwnerConfirmationKey,
                ),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
            )

            if (!response || response.requestError !== null) {
                return
            }

            this.props.navigate(
                '/resident/general-meeting/unit-owner-confirmation',
                {
                    state: {
                        unitOwnerConfirmationKey: this.state.unitOwnerConfirmationKey,
                    },
                },
            )
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    handleBackToVerificationMethodSelectionButtonClick = () => {
        this.props.navigate(
            '/resident/general-meeting/unit-owner-confirmation-verification-method-selection',
            {
                state: {
                    rootPageUrl: this.props.location.state.rootPageUrl,
                },
            },
        )
    }

    render(): ReactNode {
        return <ContentArea>
            <PlainBackLink
                to="/resident/general-meeting/unit-owner-confirmation-verification-method-selection"
                state={{
                    rootPageUrl: this.props.location.state.rootPageUrl,
                }}
            >
                戻る
            </PlainBackLink>
            <StyledDisplayItemDividerForPageTop/>
            <HideInSP>
                <PPStepper steps={unitOwnerConfirmationStepsForPc} activeStep={1}/>
            </HideInSP>
            <HideInPC>
                <PPStepper steps={unitOwnerConfirmationStepsForSp} activeStep={1} alternativeLabel/>
            </HideInPC>
            <UnitOwnerStyledPageTitle>区分所有者確認用キー入力</UnitOwnerStyledPageTitle>
            <DetailMessage>
                別途配布された用紙に記載された「区分所有者の確認用キー」を入力し、区分所有者の確認に進んでください。
            </DetailMessage>
            <UnitOwnerWideWhiteArea>
                <NatResidentApplicationDetailFormItem
                    title={'区分所有者確認用キー'}
                    required
                    component={
                        <>
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 12,
                                    name: 'unitOwnerConfirmationKey',
                                    value: this.state.unitOwnerConfirmationKey,
                                    placeholder: '確認用キーを入力',
                                }}
                                onChange={this.handleUnitOwnerConfirmationKeyInputChange}
                                eventKeys={['unitOwnerConfirmationKey']}
                                eventEmitter={this.props.eventEmitter}
                            />
                            <InputGuide>※用紙に記載された12桁の半角英数字を入力してください。</InputGuide>
                        </>
                    }
                />
            </UnitOwnerWideWhiteArea>
            <ConfirmButton
                disabled={!this.state.unitOwnerConfirmationKey}
                onClick={this.handleConfirmButtonClick}
            >
                確認へ進む
            </ConfirmButton>
            <BackToVerificationMethodSelectionButton
                onClick={this.handleBackToVerificationMethodSelectionButtonClick}
            >
                確認方法の選択へ戻る
            </BackToVerificationMethodSelectionButton>
            <Box height={'15px'}/>
        </ContentArea>
    }
}

export default createResidentWithErrorHandlingPage(ResidentUnitOwnerConfirmationKeyInputPage)

const ContentArea = styled.div`
  padding: 0 0 15px 0;
`

const StyledDisplayItemDividerForPageTop = styled(DisplayItemDividerForPageTop)`
  margin-top: 14px;
  margin-bottom: 15px;
`

const UnitOwnerStyledPageTitle = styled(StyledPageTitle)`
  margin-top: 30px;
`

const DetailMessage = styled.div`
  margin-top: 20px;
`

const UnitOwnerWideWhiteArea = styled(WideWhiteArea)`
  margin-top: 20px;
  padding: 20px 15px;
`

const InputGuide = styled.div`
  margin-top: 10px;
  font-size: ${FontSize.Small};
  color: ${Colors.AccentDark};
  ${currentMedia.lessThan('sp')`
        margin-top: 5px;
    `}
`

const ConfirmButton = styled(PrimaryStyledButton)`
  margin-top: 20px;
`

const BackToVerificationMethodSelectionButton = styled(StyledSecondaryButton)`
  margin-top: 30px;
`
